/**
 * Theme Switcher
 * Dark mode support with system theme detection
 */

(function() {
    'use strict';

    const THEME_STORAGE_KEY = 'toolsbox-theme';
    const THEME_OPTIONS = {
        LIGHT: 'light',
        DARK: 'dark',
        SYSTEM: 'system'
    };

    // 缓存当前偏好，避免频繁读取 localStorage
    let cachedPreference = null;

    /**
     * Get the user's theme preference from localStorage
     * @returns {string} The theme preference (light, dark, or system)
     */
    function getThemePreference() {
        if (cachedPreference !== null) {
            return cachedPreference;
        }
        try {
            const stored = localStorage.getItem(THEME_STORAGE_KEY);
            if (stored && Object.values(THEME_OPTIONS).includes(stored)) {
                cachedPreference = stored;
                return stored;
            }
        } catch (e) {
            console.warn('Unable to access localStorage');
        }
        cachedPreference = THEME_OPTIONS.SYSTEM;
        return THEME_OPTIONS.SYSTEM;
    }

    /**
     * Check if system prefers dark mode
     * @returns {boolean}
     */
    function systemPrefersDark() {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    /**
     * Get the effective theme (actual light/dark based on preference and system)
     * @returns {string} 'light' or 'dark'
     */
    function getEffectiveTheme() {
        const preference = getThemePreference();
        if (preference === THEME_OPTIONS.SYSTEM) {
            return systemPrefersDark() ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;
        }
        return preference;
    }

    /**
     * Apply the theme to the document
     * @param {string} theme - 'light' or 'dark'
     */
    function applyTheme(theme) {
        const html = document.documentElement;
        
        if (theme === THEME_OPTIONS.DARK) {
            html.classList.add('dark');
        } else {
            html.classList.remove('dark');
        }

        updateThemeIcon(theme);
    }

    /**
     * Update the theme toggle icon based on current theme
     * @param {string} theme - effective theme ('light' or 'dark')
     */
    function updateThemeIcon(theme) {
        const lightIcon = document.getElementById('theme-icon-light');
        const darkIcon = document.getElementById('theme-icon-dark');
        const systemIcon = document.getElementById('theme-icon-system');

        if (!lightIcon || !darkIcon) return;

        const preference = getThemePreference();

        // 根据偏好显示对应图标
        if (preference === THEME_OPTIONS.SYSTEM) {
            // 系统模式：显示太阳或月亮（取决于当前系统主题）
            if (theme === THEME_OPTIONS.DARK) {
                lightIcon.classList.add('hidden');
                darkIcon.classList.remove('hidden');
                if (systemIcon) systemIcon.classList.add('hidden');
            } else {
                lightIcon.classList.remove('hidden');
                darkIcon.classList.add('hidden');
                if (systemIcon) systemIcon.classList.add('hidden');
            }
        } else if (preference === THEME_OPTIONS.DARK) {
            lightIcon.classList.add('hidden');
            darkIcon.classList.remove('hidden');
            if (systemIcon) systemIcon.classList.add('hidden');
        } else {
            lightIcon.classList.remove('hidden');
            darkIcon.classList.add('hidden');
            if (systemIcon) systemIcon.classList.add('hidden');
        }
    }

    /**
     * Set and save the user's theme preference
     * @param {string} preference - light, dark, or system
     */
    function setThemePreference(preference) {
        cachedPreference = preference;
        try {
            localStorage.setItem(THEME_STORAGE_KEY, preference);
        } catch (e) {
            console.warn('Unable to save theme preference');
        }

        const effectiveTheme = getEffectiveTheme();
        applyTheme(effectiveTheme);
    }

    /**
     * Cycle through themes, skipping visually identical options
     * system -> light -> dark -> system (but skip if same visual effect)
     */
    function cycleTheme() {
        const currentPreference = getThemePreference();
        const systemDark = systemPrefersDark();
        let nextPreference;

        if (currentPreference === THEME_OPTIONS.SYSTEM) {
            // system -> light or dark (skip same visual)
            nextPreference = systemDark ? THEME_OPTIONS.LIGHT : THEME_OPTIONS.DARK;
        } else if (currentPreference === THEME_OPTIONS.LIGHT) {
            // light -> dark (if system is dark, system == dark, so go to system)
            nextPreference = systemDark ? THEME_OPTIONS.SYSTEM : THEME_OPTIONS.DARK;
        } else {
            // dark -> light (if system is light, system == light, so go to system)
            nextPreference = systemDark ? THEME_OPTIONS.LIGHT : THEME_OPTIONS.SYSTEM;
        }

        setThemePreference(nextPreference);
    }

    /**
     * Initialize the theme on page load
     */
    function initTheme() {
        // Apply theme immediately to prevent flash
        const effectiveTheme = getEffectiveTheme();
        applyTheme(effectiveTheme);

        // Listen for system theme changes
        if (window.matchMedia) {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            
            // Modern browsers
            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', function(e) {
                    if (getThemePreference() === THEME_OPTIONS.SYSTEM) {
                        applyTheme(e.matches ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT);
                    }
                });
            } else if (mediaQuery.addListener) {
                // Older browsers
                mediaQuery.addListener(function(e) {
                    if (getThemePreference() === THEME_OPTIONS.SYSTEM) {
                        applyTheme(e.matches ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT);
                    }
                });
            }
        }

        // Bind click event to theme toggle button using event delegation
        // This works better with type="module" scripts
        document.addEventListener('click', function(e) {
            const themeToggle = e.target.closest('#theme-toggle');
            if (themeToggle) {
                cycleTheme();
            }
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initTheme);
    } else {
        initTheme();
    }

    // Expose API for external use
    window.ThemeSwitcher = {
        getPreference: getThemePreference,
        setPreference: setThemePreference,
        getEffectiveTheme: getEffectiveTheme,
        cycle: cycleTheme,
        options: THEME_OPTIONS
    };

})();
